import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogIn, Eye, EyeOff } from 'lucide-react';
import { useLoginMutation } from '../../api/authApi';
import { setTokens } from './authSlice';

export default function LoginPage() {
  const [emailOrUserName, setEmailOrUserName] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState(null);
  const [login, { isLoading }] = useLoginMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  const submit = async (e) => {
    e.preventDefault();
    setError(null);

    // Static receptionist persona — purely frontend, no API call required.
    if (emailOrUserName === 'Reception' && password === 'Reception12345') {
      dispatch(setTokens({
        accessToken: `mock-receptionist-token-${Date.now()}`,
        refreshToken: null,
        accessTokenExpiresAt: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(),
        refreshTokenExpiresAt: null,
        user: {
          id: 'mock-reception-001',
          name: 'Reception Desk',
          username: 'Reception',
          roles: ['Receptionist'],
        },
      }));
      navigate(from, { replace: true });
      return;
    }

    try {
      const data = await login({ emailOrUserName, password }).unwrap();
      dispatch(setTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        accessTokenExpiresAt: data.accessTokenExpiresAt,
        refreshTokenExpiresAt: data.refreshTokenExpiresAt,
        user: data.user,
      }));
      navigate(from, { replace: true });
    } catch (err) {
      setError(err?.data?.error?.message || 'Login failed. Check your credentials.');
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-art">
        <img src="/logo.png" alt="" className="auth-art-watermark" aria-hidden="true" />
        <div className="auth-art-brand">
          <img src="/logo.png" alt="" className="auth-logo-sm" /> The Healing Clinic
        </div>
        <div className="auth-art-center">
          <img src="/logo.png" alt="The Healing Clinic" className="auth-logo-lg" />
          <h1>Your clinic operations, all in one place.</h1>
          <p>Leads, consultations, appointments, treatments, sessions, billing and patient communications — managed end to end.</p>
        </div>
        <div className="auth-art-foot">© {new Date().getFullYear()} The Healing Clinic CRM</div>
      </div>
      <div className="auth-form-wrap">
        <form className="auth-form" onSubmit={submit}>
          <img src="/logo.png" alt="" className="auth-form-logo" />
          <h2>Sign in</h2>
          <div className="auth-sub">Use your staff credentials to continue.</div>
          {error && <div className="auth-error">{error}</div>}
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label className="form-label">Username or email</label>
            <input
              className="form-control" autoFocus autoComplete="username"
              value={emailOrUserName} onChange={(e) => setEmailOrUserName(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 24 }}>
            <label className="form-label">Password</label>
            <div className="auth-pw-wrap">
              <input
                className="form-control" type={showPw ? 'text' : 'password'} autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button" className="auth-pw-toggle"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? 'Hide password' : 'Show password'}
                title={showPw ? 'Hide password' : 'Show password'}
              >
                {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </div>
          <button type="submit" className="btn btn-primary btn-lg" disabled={isLoading} style={{ width: '100%', justifyContent: 'center' }}>
            <LogIn size={16} /> {isLoading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
